import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglass, faUpload } from '@fortawesome/free-solid-svg-icons';
import { RequiredDocumentTypes } from '../../../components/TransactionDocuments/RequiredDocumentTypes/RequiredDocumentTypes';
import {
    isPostingParty,
    TransactionDocumentsStatusBadge,
} from '../../../components/TransactionDocuments/TransactionDocumentsStatusBadge/TransactionDocumentsStatusBadge';
import { TransactionDocumentVersionsTable } from '../../../components/TransactionDocuments/TransactionDocumentVersionsTable/TransactionDocumentVersionsTable';
import { UploadTransactionDocumentModal } from '../../../components/TransactionDocuments/UploadTransactionDocumentModal/UploadTransactionDocumentModal';
import { EditRequiredDocumentTypesModal } from '../../../components/TransactionDocuments/EditRequiredDocumentTypesModal/EditRequiredDocumentTypesModal';
import { TransactionDocumentVersion } from '../../../models/TransactionDocuments/transaction-documents.model';
import { DownloadTransactionDocument } from '../../../slices/marketplace/transaction-documents/thunks/download-transaction-document.thunk';
import { UploadTransactionDocument } from '../../../slices/marketplace/transaction-documents/thunks/upload-transaction-document.thunk';
import { SaveRequiredDocumentTypes } from '../../../slices/marketplace/transaction-documents/thunks/save-required-document-types.thunk';
import { AddCommonDocumentType } from '../../../slices/marketplace/transaction-documents/thunks/add-common-document-type.thunk';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import Helpers from '../../../utils/helper';
import { useTransactionDocuments } from './useTransactionDocuments';
import './TransactionDocumentsTab.scss';

export interface TransactionDocumentsTabProps {
    engagementId: number;
    isReadonly?: boolean;
}

const OBJECT_URL_LIFETIME_MS = 60000;

const downloadBlobAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
};

export const TransactionDocumentsTab = (props: TransactionDocumentsTabProps) => {
    const dispatch = useDispatch() as any;
    const { transactionDocuments, isLoading } = useTransactionDocuments(
        props.engagementId
    );
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isEditListModalOpen, setIsEditListModalOpen] = useState(false);

    if (isLoading) {
        return Helpers.renderTableLoader();
    }

    if (!transactionDocuments) {
        return null;
    }

    const downloadVersion = async (version: TransactionDocumentVersion) => {
        const action = await dispatch(
            DownloadTransactionDocument({
                engagementId: props.engagementId,
                versionId: version.id,
            })
        );
        if (DownloadTransactionDocument.fulfilled.match(action)) {
            downloadBlobAsFile(action.payload, version.fileName);
        }
    };

    const uploadVersion = async (
        documentType: string,
        file: File,
        comment: string
    ) => {
        const action = await dispatch(
            UploadTransactionDocument({
                engagementId: props.engagementId,
                documentType,
                file,
                comment: comment || undefined,
            })
        );
        return UploadTransactionDocument.fulfilled.match(action);
    };

    const saveRequiredDocumentTypes = async (documentTypes: string[]) => {
        const action = await dispatch(
            SaveRequiredDocumentTypes({
                engagementId: props.engagementId,
                documentTypes,
            })
        );
        return SaveRequiredDocumentTypes.fulfilled.match(action);
    };

    const addCommonDocumentType = async (name: string) => {
        const action = await dispatch(
            AddCommonDocumentType({
                engagementId: props.engagementId,
                name,
            })
        );
        return AddCommonDocumentType.fulfilled.match(action);
    };

    const renderWaitingForBuyerDrafts = () => (
        <div className="transaction-documents-tab__empty">
            <FontAwesomeIcon
                icon={faHourglass}
                className="transaction-documents-tab__empty-icon"
            />
            <h4 className="transaction-documents-tab__empty-title">
                Waiting for buyer drafts
            </h4>
            <p className="transaction-documents-tab__empty-text">
                The buyer is preparing the initial drafts. All drafts will appear here
                at the same time once the buyer posts the full package, and you'll be
                notified by email.
            </p>
        </div>
    );

    return (
        <div className="transaction-documents-tab marketplace-dynamic-container">
            <h3 className="transaction-documents-tab__title">TRANSACTION DOCUMENTS</h3>
            <RequiredDocumentTypes
                requiredDocumentTypes={transactionDocuments.requiredDocumentTypes}
                lastEditedByName={transactionDocuments.requiredListLastEditedByName}
                lastEditedAt={transactionDocuments.requiredListLastEditedAt}
                canEdit={transactionDocuments.canEditRequiredList && !props.isReadonly}
                onEdit={() => setIsEditListModalOpen(true)}
            />
            <div className="transaction-documents-tab__summary">
                <div className="transaction-documents-tab__status">
                    <TransactionDocumentsStatusBadge
                        requiredDocumentTypes={transactionDocuments.requiredDocumentTypes}
                        versions={transactionDocuments.versions}
                        currentUserParty={transactionDocuments.currentUserParty}
                    />
                    <span className="transaction-documents-tab__count">
                        {transactionDocuments.versions.length === 1
                            ? '1 document version'
                            : `${transactionDocuments.versions.length} document versions`}
                    </span>
                </div>
                {transactionDocuments.canUpload && !props.isReadonly && (
                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setIsUploadModalOpen(true)}>
                        <FontAwesomeIcon icon={faUpload} /> Upload Document
                    </button>
                )}
            </div>
            <TransactionDocumentVersionsTable
                versions={transactionDocuments.versions}
                onDownload={downloadVersion}
                noDataIndication={
                    isPostingParty(transactionDocuments.currentUserParty)
                        ? undefined
                        : renderWaitingForBuyerDrafts
                }
            />
            <UploadTransactionDocumentModal
                isOpen={isUploadModalOpen}
                requiredDocumentTypes={transactionDocuments.requiredDocumentTypes}
                onClose={() => setIsUploadModalOpen(false)}
                onUpload={uploadVersion}
            />
            <EditRequiredDocumentTypesModal
                isOpen={isEditListModalOpen}
                requiredDocumentTypes={transactionDocuments.requiredDocumentTypes}
                documentTypesWithUploads={transactionDocuments.documentTypesWithUploads}
                commonDocumentTypes={transactionDocuments.commonDocumentTypes}
                isConfirmingSave={
                    transactionDocuments.currentUserParty === TimelineParty.Buyer &&
                    !transactionDocuments.isRequiredListConfirmed
                }
                canAddCommonDocumentTypes={transactionDocuments.canAddCommonDocumentTypes}
                onClose={() => setIsEditListModalOpen(false)}
                onSave={saveRequiredDocumentTypes}
                onAddCommonDocumentType={addCommonDocumentType}
            />
        </div>
    );
};
